import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import {
    PageContentWrapper,
    PageHeader,
    PageDescription,
} from '../main/LayoutStyles';
import {
    NotebookPageWrapper,
    NotebookLayout,
    CalendarColumn,
    NotesColumn,
    CalendarWrapper,
    NoteEditorWrapper,
    DateHeader,
    NotesList,
    NoteItem,
    NewNoteSection,
    NoteTextarea,
    AddNoteButton,
    MoodTrackerWrapper,
    MoodSelector,
    MoodButton,
} from './NotebookStyles';
import {
    Smile, Frown, Meh, Laugh, Angry, Trash2, PlusCircle,
    TrendingUp, Moon, Zap, Coffee, Bed, AlertTriangle, Save
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import apiClient from '../services/api';
import { StyledButton, AlertBox } from '../main/CommonStyles';

const generalMoods = [
    { name: 'terrible', icon: <Angry />, label: 'Okropny', color: '#EF5350', bgColor: '#FFCDD2' },
    { name: 'bad', icon: <Frown />, label: 'Zły', color: '#FF7043', bgColor: '#FFCCBC' },
    { name: 'neutral', icon: <Meh />, label: 'Neutralny', color: '#FFEE58', bgColor: '#FFF9C4' },
    { name: 'good', icon: <Smile />, label: 'Dobry', color: '#9CCC65', bgColor: '#E6EE9C' },
    { name: 'great', icon: <Laugh />, label: 'Świetny', color: '#66BB6A', bgColor: '#C8E6C9' },
];
const productivityLevels = [
    { name: 'very_low', icon: <Frown />, label: 'Bardzo niska', color: '#EF5350', bgColor: '#FFCDD2' },
    { name: 'low', icon: <Meh />, label: 'Niska', color: '#FFB74D', bgColor: '#FFE0B2' },
    { name: 'medium', icon: <Smile />, label: 'Średnia', color: '#FFEE58', bgColor: '#FFF9C4' },
    { name: 'high', icon: <TrendingUp />, label: 'Wysoka', color: '#66BB6A', bgColor: '#C8E6C9' },
    { name: 'very_high', icon: <Zap />, label: 'Bardzo wysoka', color: '#42A5F5', bgColor: '#BBDEFB' },
];
const sleepQualityLevels = [
    { name: 'very_poor', icon: <Angry />, label: 'Bardzo słaba', color: '#EF5350', bgColor: '#FFCDD2' },
    { name: 'poor', icon: <Frown />, label: 'Słaba', color: '#FF7043', bgColor: '#FFCCBC' },
    { name: 'average', icon: <Meh />, label: 'Przeciętna', color: '#FFEE58', bgColor: '#FFF9C4' },
    { name: 'good', icon: <Bed />, label: 'Dobra', color: '#9CCC65', bgColor: '#E6EE9C' },
    { name: 'very_good', icon: <Moon />, label: 'Bardzo dobra', color: '#66BB6A', bgColor: '#C8E6C9' },
];

const formatDateToISO = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        console.error("formatDateToISO otrzymało nieprawidłową wartość daty:", date);
        return null;
    }
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const GHOST_JOURNAL_STORAGE_KEY = 'epsycholog_ghost_journal_data';

const NotebookPage = () => {
    const { currentUser, ghostUserId } = useAuth();
    const navigate = useNavigate();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [currentDayData, setCurrentDayData] = useState({
        notes: [],
        mood: null,
        productivity: null,
        sleepQuality: null,
    });
    const [newNoteText, setNewNoteText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isRatingLoading, setIsRatingLoading] = useState({});
    const [error, setError] = useState(null);
    const [allEntriesMeta, setAllEntriesMeta] = useState({});

    // === KLUCZOWA ZMIANA NR 1: Tworzymy stabilną wartość logiczną ===
    const isAuthenticated = !!currentUser;

    const loadGhostJournalData = () => {
        const data = localStorage.getItem(GHOST_JOURNAL_STORAGE_KEY);
        return data ? JSON.parse(data) : {};
    };

    const saveGhostJournalData = (allData) => {
        localStorage.setItem(GHOST_JOURNAL_STORAGE_KEY, JSON.stringify(allData));
    };

    const getGhostDayData = (dateString) => {
        const allGhostData = loadGhostJournalData();
        return allGhostData[dateString] || { notes: [], mood: null, productivity: null, sleepQuality: null };
    };

    const updateGhostDayData = (dateString, dayData) => {
        const allGhostData = loadGhostJournalData();
        allGhostData[dateString] = dayData;
        saveGhostJournalData(allGhostData);
        const newMeta = { ...allEntriesMeta };
        if (dayData.notes.length > 0 || dayData.mood || dayData.productivity || dayData.sleepQuality) {
            newMeta[dateString] = true;
        } else {
            delete newMeta[dateString];
        }
        setAllEntriesMeta(newMeta);
    };

    const fetchDayData = useCallback(async (date) => {
        setIsLoading(true);
        setError(null);
        const dateString = formatDateToISO(date);
        if (!dateString) return;

        if (isAuthenticated) {
            try {
                const response = await apiClient.get(`/journal/day/${dateString}`);
                setCurrentDayData({
                    notes: response.data.notes || [],
                    mood: response.data.mood || null,
                    productivity: response.data.productivity || null,
                    sleepQuality: response.data.sleepQuality || null,
                });
            } catch (err) {
                if (err.response && err.response.status === 404) {
                    setCurrentDayData({ notes: [], mood: null, productivity: null, sleepQuality: null });
                } else {
                    setError(err.response?.data?.message || err.message || 'Nie udało się pobrać danych dziennika.');
                    setCurrentDayData({ notes: [], mood: null, productivity: null, sleepQuality: null });
                }
            } finally {
                setIsLoading(false);
            }
        } else {
            setCurrentDayData(getGhostDayData(dateString));
            setIsLoading(false);
        }
        // === KLUCZOWA ZMIANA NR 2: Używamy stabilnej wartości 'isAuthenticated' ===
    }, [isAuthenticated]);

    const fetchAllEntriesMeta = useCallback(async () => {
        if (isAuthenticated) {
            try {
                const response = await apiClient.get('/journal/entries/meta');
                setAllEntriesMeta(response.data || {});
            } catch (err) {
                console.error("Nie udało się pobrać metadanych wpisów:", err);
                setAllEntriesMeta({});
            }
        } else {
            const allGhostData = loadGhostJournalData();
            const ghostMeta = {};
            for (const dateKey in allGhostData) {
                const day = allGhostData[dateKey];
                if (day.notes.length > 0 || day.mood || day.productivity || day.sleepQuality) {
                    ghostMeta[dateKey] = true;
                }
            }
            setAllEntriesMeta(ghostMeta);
        }
        // === KLUCZOWA ZMIANA NR 3: Używamy stabilnej wartości 'isAuthenticated' ===
    }, [isAuthenticated]);

    useEffect(() => {
        fetchDayData(selectedDate);
    }, [selectedDate, fetchDayData]);

    useEffect(() => {
        fetchAllEntriesMeta();
    }, [fetchAllEntriesMeta]);


    const handleUpdateRating = async (ratingType, ratingValue) => {
        const dateString = formatDateToISO(selectedDate);
        if (!dateString) return;
        const currentRatingValue = currentDayData[ratingType];
        const newRatingValue = currentRatingValue === ratingValue ? null : ratingValue;
        const updatedDayData = { ...currentDayData, [ratingType]: newRatingValue };
        setCurrentDayData(updatedDayData);

        if (isAuthenticated) {
            setIsRatingLoading(prev => ({ ...prev, [ratingType]: true }));
            setError(null);
            try {
                const payload = { date: dateString, ratingType: ratingType.toLowerCase(), ratingValue: newRatingValue };
                const response = await apiClient.post('/journal/rating', payload);
                setCurrentDayData({
                    notes: response.data.notes || [],
                    mood: response.data.mood || null,
                    productivity: response.data.productivity || null,
                    sleepQuality: response.data.sleepQuality || null,
                });
                fetchAllEntriesMeta();
            } catch (err) {
                setError(err.response?.data?.message || err.message || `Nie udało się zapisać oceny (${ratingType}).`);
                fetchDayData(selectedDate);
            } finally {
                setIsRatingLoading(prev => ({ ...prev, [ratingType]: false }));
            }
        } else {
            updateGhostDayData(dateString, updatedDayData);
        }
    };

    const handleAddNote = async () => {
        if (newNoteText.trim() === '') return;
        const dateString = formatDateToISO(selectedDate);
        if (!dateString) return;

        const optimisticNewNote = { id: `temp-${Date.now()}`, text: newNoteText, timestamp: new Date().toISOString() };
        setCurrentDayData(prev => ({ ...prev, notes: [...prev.notes, optimisticNewNote] }));
        const textToSave = newNoteText;
        setNewNoteText('');

        if (isAuthenticated) {
            setIsLoading(true);
            try {
                const response = await apiClient.post(`/journal/day/${dateString}/notes`, { text: textToSave });
                const savedNote = response.data;
                setCurrentDayData(prev => ({ ...prev, notes: prev.notes.map(n => n.id === optimisticNewNote.id ? savedNote : n) }));
                fetchAllEntriesMeta();
            } catch (err) {
                setError(err.response?.data?.message || err.message || 'Nie udało się dodać notatki.');
                setCurrentDayData(prev => ({ ...prev, notes: prev.notes.filter(n => n.id !== optimisticNewNote.id) }));
            } finally {
                setIsLoading(false);
            }
        } else {
            const updatedNotes = [...currentDayData.notes.filter(n => !n.id.startsWith('temp-')), optimisticNewNote];
            updateGhostDayData(dateString, { ...currentDayData, notes: updatedNotes });
        }
    };

    const handleDeleteNote = async (noteId) => {
        const dateString = formatDateToISO(selectedDate);
        if (!dateString) return;

        const originalNotes = [...currentDayData.notes];
        setCurrentDayData(prev => ({ ...prev, notes: prev.notes.filter(n => n.id !== noteId) }));

        if (isAuthenticated) {
            if (typeof noteId === 'string' && noteId.startsWith('temp-')) {
                const remainingNotes = originalNotes.filter(n => n.id !== noteId);
                updateGhostDayData(dateString, {...currentDayData, notes: remainingNotes });
                return;
            }
            setIsLoading(true);
            try {
                await apiClient.delete(`/journal/day/${dateString}/notes/${noteId}`);
                fetchAllEntriesMeta();
                fetchDayData(selectedDate);
            } catch (err) {
                setError(err.response?.data?.message || err.message || 'Nie udało się usunąć notatki.');
                setCurrentDayData(prev => ({ ...prev, notes: originalNotes }));
            } finally {
                setIsLoading(false);
            }
        } else {
            const updatedNotes = currentDayData.notes.filter(n => n.id !== noteId);
            updateGhostDayData(dateString, { ...currentDayData, notes: updatedNotes });
        }
    };

    const tileClassName = ({ date, view }) => {
        if (view === 'month') {
            const dateString = formatDateToISO(date);
            if (allEntriesMeta[dateString]) {
                return 'day-with-note';
            }
        }
        return null;
    };

    const RatingSelectorComponent = ({ title, ratingType, levels, currentRatingValue, onUpdate }) => (
        <MoodTrackerWrapper>
            <h3>{title}</h3>
            <MoodSelector>
                {levels.map(level => (
                    <MoodButton
                        key={level.name}
                        title={level.label}
                        className={`${level.name} ${currentRatingValue === level.name ? 'selected' : ''}`}
                        moodcolor={currentRatingValue === level.name ? level.bgColor : undefined}
                        onClick={() => onUpdate(ratingType, level.name)}
                        disabled={isRatingLoading[ratingType] || (isLoading && isAuthenticated)}
                    >
                        {isRatingLoading[ratingType] && currentRatingValue === level.name ? '...' : level.icon}
                    </MoodButton>
                ))}
            </MoodSelector>
        </MoodTrackerWrapper>
    );

    const GhostUserWarning = () => (
        <AlertBox type="info" style={{marginTop: '1rem'}}>
            <AlertTriangle size={24} />
            <div>
                <p><strong>Korzystasz jako gość.</strong></p>
                <p>
                    Twoje wpisy w dzienniku są zapisywane tylko lokalnie w tej przeglądarce i nie będą dostępne na innych urządzeniach ani po wyczyszczeniu danych przeglądarki.
                    Aby zapisać swoje dane na stałe i mieć do nich dostęp zewsząd,
                    <RouterLink to="/login" style={{fontWeight: 'bold'}}> zaloguj się</RouterLink> lub
                    <RouterLink to="/register" style={{fontWeight: 'bold'}}> zarejestruj</RouterLink>.
                </p>
            </div>
        </AlertBox>
    );

    return (
        <PageContentWrapper className="custom-scrollbar">
            <PageHeader>Mój Dziennik</PageHeader>
            <PageDescription>
                Zapisuj swoje myśli, uczucia i oceniaj samopoczucie, produktywność oraz jakość snu każdego dnia.
                {!isAuthenticated && " Pamiętaj, że jako gość, Twoje dane są zapisywane tylko lokalnie."}
            </PageDescription>

            {!isAuthenticated && <GhostUserWarning />}
            {error && <AlertBox type="error">{error}</AlertBox>}

            <NotebookPageWrapper>
                <NotebookLayout>
                    <CalendarColumn>
                        <CalendarWrapper>
                            <Calendar
                                onChange={setSelectedDate}
                                value={selectedDate}
                                locale="pl-PL"
                                tileClassName={tileClassName}
                            />
                        </CalendarWrapper>

                        <RatingSelectorComponent
                            title="Jak się dzisiaj czujesz?"
                            ratingType="mood"
                            levels={generalMoods}
                            currentRatingValue={currentDayData.mood}
                            onUpdate={handleUpdateRating}
                        />
                        <RatingSelectorComponent
                            title="Jaki był Twój poziom produktywności?"
                            ratingType="productivity"
                            levels={productivityLevels}
                            currentRatingValue={currentDayData.productivity}
                            onUpdate={handleUpdateRating}
                        />
                        <RatingSelectorComponent
                            title="Jak oceniasz jakość swojego snu?"
                            ratingType="sleepQuality"
                            levels={sleepQualityLevels}
                            currentRatingValue={currentDayData.sleepQuality}
                            onUpdate={handleUpdateRating}
                        />
                    </CalendarColumn>

                    <NotesColumn>
                        <NoteEditorWrapper>
                            <DateHeader>{selectedDate.toLocaleDateString('pl-PL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</DateHeader>

                            {isLoading && <p>Ładowanie danych dnia...</p>}

                            <NotesList className="custom-scrollbar">
                                {currentDayData.notes.length > 0 ? (
                                    currentDayData.notes.map(note => (
                                        <NoteItem key={note.id}>
                                            <p>{note.text}</p>
                                            <div>
                                                <button title="Usuń" onClick={() => handleDeleteNote(note.id)} disabled={isLoading}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </NoteItem>
                                    ))
                                ) : (
                                    !isLoading && <p style={{ textAlign: 'center', color: '#999', marginTop: '1rem' }}>Brak notatek dla tego dnia.</p>
                                )}
                            </NotesList>

                            <NewNoteSection>
                                <NoteTextarea
                                    placeholder="Dodaj nową notatkę..."
                                    value={newNoteText}
                                    onChange={(e) => setNewNoteText(e.target.value)}
                                    className="custom-scrollbar"
                                    disabled={isLoading && isAuthenticated}
                                />
                                <AddNoteButton onClick={handleAddNote} disabled={(newNoteText.trim() === '') || (isLoading && isAuthenticated)}>
                                    <PlusCircle size={16} style={{ marginRight: '4px' }} /> Dodaj Notatkę
                                </AddNoteButton>
                            </NewNoteSection>
                            {!isAuthenticated && (
                                <StyledButton
                                    onClick={() => navigate('/login', { state: { message: "Zaloguj się, aby zapisać swoje wpisy w dzienniku na stałe." } })}
                                    style={{ marginTop: '1rem', width: '100%', backgroundColor: '#f0ad4e' }}
                                >
                                    <Save size={16} style={{ marginRight: '8px' }} /> Zaloguj się, aby zapisać zmiany na serwerze
                                </StyledButton>
                            )}
                        </NoteEditorWrapper>
                    </NotesColumn>
                </NotebookLayout>
            </NotebookPageWrapper>
        </PageContentWrapper>
    );
};

export default NotebookPage;